import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { setAuthToken, setUnauthorizedHandler } from '../api/client'
import { authStorage } from './storage'

/**
 * Who is signed in, if anyone.
 *
 * SellGPT signs a customer into their EXISTING Cashit account. It does not have accounts of its own: the
 * mobile number a customer verifies here is the same number that identifies them on cashit.in and in the
 * apps, and a number that already belongs to a Cashit customer signs into that customer rather than
 * creating a second one. That is enforced by the server - /auth/register refuses a mobile number that is
 * already registered - and this store simply follows what /auth/verify-otp says.
 */

export interface Customer {
  customerRegId: number
  customerNo?: string
  name: string
  mobileNumber: string
  token: string
}

interface AuthContextValue {
  customer: Customer | null
  /** True while the stored session is being read, so the sidebar does not flash "Guest" for a signed-in user. */
  loading: boolean
  /** Set when a session was dropped because the server rejected its token, so the UI can say why. */
  expired: boolean
  signIn: (customer: Customer) => void
  signOut: () => void
  dismissExpiry: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)

  // Restored before the first paint that matters. The token is handed to the API client immediately so a
  // request fired by a child on mount is already authenticated.
  useEffect(() => {
    const stored = authStorage.read<Customer>()
    if (stored?.token && stored.customerRegId) {
      setAuthToken(stored.token)
      setCustomer(stored)
    }
    setLoading(false)
  }, [])

  const signOut = useCallback(() => {
    setAuthToken(null)
    authStorage.clear()
    setCustomer(null)
    // The sale draft is deliberately NOT cleared here. Signing out - or being signed out by an expired
    // token - must not throw away a device the customer spent five minutes describing; they sign back in
    // and carry on from where they were.
  }, [])

  const signIn = useCallback((next: Customer) => {
    setAuthToken(next.token)
    authStorage.write(next)
    setCustomer(next)
    setExpired(false)
  }, [])

  // A 401 from any endpoint means the token is gone or no longer valid. Handled centrally so every screen
  // behaves the same way, and so a stale token cannot sit in storage failing request after request.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setExpired(true)
      signOut()
    })
    return () => setUnauthorizedHandler(null)
  }, [signOut])

  const value = useMemo<AuthContextValue>(
    () => ({ customer, loading, expired, signIn, signOut, dismissExpiry: () => setExpired(false) }),
    [customer, loading, expired, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
